import { ApolloClient, ApolloLink, CombinedGraphQLErrors, HttpLink } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { addBreadcrumb } from "@appsignal/browser"

import { apolloCache } from "#Utils/apollo-cache"

const AUTH_ERROR_PATTERNS = [
  /request could not be authenticated/i,
  /authentication failed/i,
  /unauthorized/i,
  /received status code 401/i,
]

const isAuthError = (message) => {
  return AUTH_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

const customFetch = (uri, options) => {
  if (process.env.NODE_ENV !== "test") {
    const body = JSON.parse(options.body)

    appsignal.addBreadcrumb({
      category: "graphql",
      action: `GraphQL query: ${body.operationName}`,
    })

    addBreadcrumb({
      category: "graphql",
      message: `GraphQL query: ${body.operationName}`,
    })
  }

  return fetch(uri, options)
}

const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    onError(({ error }) => {
      // Apollo v4: errors are now unified under a single `error` property
      // Use static `.is()` methods to check error types
      if (CombinedGraphQLErrors.is(error)) {
        // Handle GraphQL errors
        error.errors.forEach(({ message: originalMessage, locations, path }) => {
          const errorMessage = `[GraphQL error]: Message: ${originalMessage}, Location: ${locations}, Path: ${path}`

          try {
            const err = new Error(errorMessage)

            if (isAuthError(originalMessage)) {
              appsignal.sendError(err, (span) => {
                span.setAction("GraphQL Auth Error")
                span.setNamespace("GraphQL Auth")
                span.setTags({ errorType: "authentication" })
              })
            } else {
              appsignal.sendError(err, (span) => {
                span.setAction("GraphQL Error")
                span.setNamespace("GraphQL")
                span.setTags({ errorType: "graphql" })
              })
            }
          } catch {
            console.warn(errorMessage)
          }
        })
      } else if (error) {
        // Handle network/other errors
        const errorMessage = `[GraphQL Network error]: ${error.message || error}`

        try {
          const err = new Error(errorMessage)

          if (isAuthError(errorMessage)) {
            appsignal.sendError(err, (span) => {
              span.setAction("GraphQL Auth Error")
              span.setNamespace("GraphQL Auth")
              span.setTags({ errorType: "authentication" })
            })
          } else {
            appsignal.sendError(err, (span) => {
              span.setAction("GraphQL Network Error")
              span.setNamespace("GraphQL Network")
              span.setTags({ errorType: "network" })
            })
          }
        } catch {
          console.warn(errorMessage)
        }
      }
    }),
    new HttpLink({
      uri: "/graphql",
      credentials: "same-origin",
      headers: { "client-name": "AppSignal.com" },
    }),
  ]),
  cache: apolloCache,
  fetch: customFetch,
})

const apolloTestClient = new ApolloClient({
  link: ApolloLink.from([
    onError(({ error }) => {
      // Log Apollo errors in test environment to debug
      if (error) {
        console.error("[Test] GraphQL error:", error)
      }
    }),
    new HttpLink({
      uri: "http://localhost/graphql",
      credentials: "same-origin",
      headers: { "client-name": "AppSignal.com" },
    }),
  ]),
  cache: apolloCache,
  // Let MSW intercept fetch calls in test environment
})

apolloTestClient.defaultOptions = {
  watchQuery: {
    fetchPolicy: "network-only",
  },
  query: {
    fetchPolicy: "network-only",
  },
  // Note: Don't set errorPolicy: "all" for mutations as it prevents errors from throwing
  // which is the expected behavior in most components
}

export { apolloClient, apolloTestClient }
